import type { ComponentProps, ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * A value with a dotted underline whose tooltip says where it comes from (the POs behind "On
 * order", the prices behind a unit price). Keyboard users reach it with Tab and the tooltip opens on
 * focus, as it does on hover. Inside a DataTable row with a row click, the row click still works.
 */
export function ExplainedValue({
  explanation,
  children,
  side = 'top',
  numeric = false,
  className,
  contentClassName,
}: {
  /** The tooltip: a sentence, or a short list. */
  explanation: ReactNode;
  children: ReactNode;
  side?: ComponentProps<typeof TooltipContent>['side'];
  /** tabular-nums, for a figure in a column. */
  numeric?: boolean;
  className?: string;
  /** Width or layout of the tooltip (max-w-sm, whitespace-pre-line). */
  contentClassName?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          // Focusable so keyboard users get the explanation too (the shared trigger opens on keyboard focus).
          tabIndex={0}
          className={cn(
            'cursor-help rounded-sm underline decoration-dotted decoration-muted-foreground/70 underline-offset-2 focus-visible:outline-2 focus-visible:outline-ring',
            numeric && 'tabular-nums',
            className
          )}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent side={side} sideOffset={4} className={cn('max-w-xs text-left', contentClassName)}>
        {explanation}
      </TooltipContent>
    </Tooltip>
  );
}
